import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { Button, Divider, Form, Icon, Modal } from "semantic-ui-react";
import TourLayoutWrapper from "../components/TourLayoutWrapper";
import TourNav from "../constants/TourNav";
import FormRadio from "../components/FormRadio";
import SaveButton from "../components/SaveButton";
import TourStore from "../../../stores/TourStore";
import ModalUnsavedConfirmation from "~/components/element/ModalUnsavedConfirmation";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import ModalError from "../../../components/element/ModalError";
import "../styles/Stats.less";
import TourStats from "../components/TourStats";
import TourStatsEmail from "../components/TourStatsEmail";
import ContentLoader from "~/components/element/ContentLoader";
import StickyMenu from "~/components/StickyMenu";
import ToastMessageStore from "~/stores/ToastMessageStore";

@observer
class Stats extends Component {
  constructor(props) {
    super(props);
    document.title = "Tour - Stats";
    this.state = { sendingEmailModal: false };
  }

  componentDidMount() {
    const { tourStore, tourId } = this.props;
    tourStore.loadStats(tourId);
    window.scrollTo(0, 0);
  }

  sendEmail = () => {
    const { tourStore, tourId } = this.props;
    tourStore.sendStatsEmail(tourId).then(() => this.setState({ sendingEmailModal: true }));
  };

  save() {
    const { t, tourStore, toastMessageStore } = this.props;

    tourStore.save().then(() => {
      toastMessageStore.addToastMessage(t("changesSaved"), "positive", t("success"));
    });
  }

  render() {
    const { tourStore, tourId, t, tourLayoutContentContainerRef } = this.props;
    const { isLoading, isSaving, /** @type {TourStatistic} */ tourStatistic } = tourStore;
    const { sendingEmailModal } = this.state;
    if (isLoading) {
      return <ContentLoader />;
    }
    return (
      <>
        <ModalUnsavedConfirmation
          when={tourStore.hasUnsavedData}
          onSave={() => this.save()}
          onDiscard={() => tourStore.reset()}
        />
        <LoadingWrapper loading={isSaving} message={t("saving")} />
        <ModalError nestStore={tourStore} />
        <StickyMenu
          contextRef={tourLayoutContentContainerRef}
          headerText={t("clientPanelMisc:stats")}
          button={<SaveButton disabled={!tourStore.hasUnsavedData} onClick={() => this.save()} />}
        />

        <h4 className="stats-h4">{t("clientPanelStats:statsSummary")}</h4>
        {tourStatistic.tourId === tourId && (
          <>
            <TourStats tourStatistic={tourStatistic} />
            <div className="stats-view-btn-wrap">
              <Button
                className="stats-view-btn"
                icon
                onClick={() => {
                  window.open(
                    `//${window.location.host}/public/stats/${tourId}?locale=${window.templateDefaults.locale}`,
                  );
                }}
              >
                {t("clientPanelStats:viewFullStats")} <Icon name="caret right" />
              </Button>
            </div>
          </>
        )}
        <Modal size="mini" open={sendingEmailModal} onClose={() => this.setState({ sendingEmailModal: false })}>
          <Modal.Header>{t("clientPanelStats:tourStatisticsEmailSent")}</Modal.Header>
          <Modal.Actions>
            <Button positive content={t("ok")} onClick={() => this.setState({ sendingEmailModal: false })} />
          </Modal.Actions>
        </Modal>

        <Divider className="stats-divider" />

        <Form className="stats-section-form">
          <div>
            <h4>{t("clientPanelStats:enableStatsEmails")}</h4>
          </div>
          <div>
            <label className="stats-section-tagline">
              <em>{t("clientPanelStats:thisWillEnableDisableStatsEmails")}</em>
            </label>
          </div>
          <div className="stats-input-container">
            <FormRadio
              dataStore={tourStore}
              name="tour.sendTourStatsToCustomer"
              defaultValue={tourStore.tourLoaded ? tourStore.tour.customerDefaults.sendTourStatsToCustomer : undefined}
              value
              label={t("clientPanelStats:enableCustomerStatsEmails")}
            />
            <FormRadio
              dataStore={tourStore}
              defaultValue={tourStore.tourLoaded ? tourStore.tour.customerDefaults.sendTourStatsToCustomer : undefined}
              name="tour.sendTourStatsToCustomer"
              value={false}
              label={t("clientPanelStats:disableCustomerStatsEmails")}
            />
          </div>
          <Divider className="stats-divider" />
          <div className="stats-input-container">
            <TourStatsEmail tourStore={tourStore} />
          </div>
          <Button size="medium" icon onClick={this.sendEmail}>
            <Icon name="mail" /> {t("clientPanelStats:sendTourStatsEmailNow")}
          </Button>
          <Divider className="stats-divider" />

          <div>
            <h4>{t("clientPanelStats:statsEmailsFrequency")}</h4>
          </div>
          <div className="stats-input-container">
            <FormRadio
              dataStore={tourStore}
              name="tour.sendTourStatsEveryNDays"
              value={0}
              defaultValue={tourStore.tourLoaded ? tourStore.tour.customerDefaults.sendTourStatsEveryNDays : undefined}
              label={t("never")}
            />
            <FormRadio
              dataStore={tourStore}
              name="tour.sendTourStatsEveryNDays"
              value={7}
              defaultValue={tourStore.tourLoaded ? tourStore.tour.customerDefaults.sendTourStatsEveryNDays : undefined}
              label={t("weekly")}
            />
            <FormRadio
              dataStore={tourStore}
              name="tour.sendTourStatsEveryNDays"
              value={30}
              defaultValue={tourStore.tourLoaded ? tourStore.tour.customerDefaults.sendTourStatsEveryNDays : undefined}
              label={t("monthly")}
            />
          </div>
        </Form>
      </>
    );
  }
}

Stats.propTypes = {
  tourStore: PropTypes.instanceOf(TourStore),
  tourId: PropTypes.number.isRequired,
  t: PropTypes.func.isRequired,
  tourLayoutContentContainerRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(TourLayoutWrapper(Stats, TourNav.STATS));
